import type { App } from 'vue'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import ToastService from 'primevue/toastservice'

/**
 * Tema A&R Abogados — paleta "dorado togado" sobre superficies pizarra.
 * Redefinimos el color `primary` del preset Aura a una rampa dorada (#C8AA5A),
 * con lo que todas las utilidades Tailwind `primary-*` (vía tailwindcss-primeui)
 * y los componentes PrimeVue quedan recoloreados de forma coherente.
 */
const ArAbogados = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fbf7ec',
      100: '#f5eccf',
      200: '#ebd9a0',
      300: '#dfc07a',
      400: '#d3ad62',
      500: '#c8aa5a',
      600: '#a88a3a',
      700: '#856b2d',
      800: '#6b5626',
      900: '#5a4922',
      950: '#332810',
    },
    colorScheme: {
      light: {
        primary: {
          color: '#a88a3a',
          contrastColor: '#ffffff',
          hoverColor: '#856b2d',
          activeColor: '#6b5626',
        },
      },
      dark: {
        primary: {
          color: '#c8aa5a',
          contrastColor: '#111827',
          hoverColor: '#dfc07a',
          activeColor: '#ebd9a0',
        },
      },
    },
  },
})

export function setupPrimeVue(app: App): void {
  app.use(PrimeVue, {
    theme: {
      preset: ArAbogados,
      options: {
        prefix: 'p',
        darkModeSelector: '.dark',
        cssLayer: {
          name: 'primevue',
          order: 'tailwind-base, primevue, tailwind-utilities',
        },
      },
    },
    ripple: true,
    locale: {
      accept: 'Aceptar',
      reject: 'Cancelar',
      choose: 'Elegir',
      upload: 'Cargar',
      cancel: 'Cancelar',
      completed: 'Completado',
      pending: 'Pendiente',
      fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
      dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
      dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
      monthNames: [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ],
      monthNamesShort: [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic',
      ],
      today: 'Hoy',
      clear: 'Limpiar',
      weekHeader: 'Sem',
      firstDayOfWeek: 1,
      dateFormat: 'dd/mm/yy',
      weak: 'Débil',
      medium: 'Medio',
      strong: 'Fuerte',
      passwordPrompt: 'Ingrese una contraseña',
      emptyMessage: 'No se encontraron resultados',
      emptyFilterMessage: 'No se encontraron resultados',
    },
  })

  app.use(ConfirmationService)
  app.use(DialogService)
  app.use(ToastService)
}
